import { useUserRole } from 'hooks/useUserRole'
import { CardButtonProps } from 'components/IntegrationsModal/components/IntegrationCards/Types'
import { Button } from 'blaze-ui/Button'
import { useModals } from 'almanac-ui/Modal'
import { ManageIntegrationAccounts } from 'components/IntegrationsModal/ManageIntegrationAccounts/ManageIntegrationAccounts'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { ArrowRefresh2, Close } from 'blaze-ui/icons'
import { ExternalIntegration, ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { useConnectedAccountCardProps } from 'components/IntegrationsModal/ManageIntegrationAccounts/hooks'
import { Spinner } from 'common/Spinner/Spinner'
import { Tooltip } from 'common/Tooltip/Tooltip'
import Styles from '../ExternalIntegrationCard/ExternalIntegrationCard.module.scss'
import cx from 'classnames'

const CardButtonsForVirtualMarketer = ({
  integration,
  isDisconnected = false,
  isOutdated = false,
}: {
  integration: ExternalIntegration
  isDisconnected?: boolean
  isOutdated?: boolean
}) => {
  const { onRefreshConnection, onDisconnectAccount, isDeleting } = useConnectedAccountCardProps(integration)

  return (
    <div className={Styles.actions}>
      {integration.provider !== ExternalIntegrationEnum.WordPress && (
        <Button
          variant="tertiary"
          size="sm"
          onPress={() => onRefreshConnection()}
          isDisabled={isDeleting}
          frontIcon={ArrowRefresh2}
          className={cx({
            [Styles.disconnectedRefreshButton]: isDisconnected,
            [Styles.outdatedRefreshButton]: isOutdated,
          })}
        >
          Refresh
        </Button>
      )}
      <Button
        variant="tertiary"
        size="sm"
        onPress={onDisconnectAccount}
        isDisabled={isDeleting}
        frontIcon={isDeleting ? undefined : Close}
      >
        {isDeleting ? (
          <div className={Styles.loadingState}>
            <Spinner />
            <span>Disconnecting...</span>
          </div>
        ) : (
          'Disconnect'
        )}
      </Button>
    </div>
  )
}

export const CardButtons = ({
  isConnected,
  onConnect,
  provider,
  integration,
  isDisabled = false,
  tooltipMessage,
  isDisconnected = false,
  isOutdated = false,
}: CardButtonProps) => {
  const userRole = useUserRole()
  const { openModal } = useModals()
  const isVirtualMarketer = useCurrentWorkspace()?.is_virtual_marketer

  if (!userRole) {
    return null
  }

  if (!isConnected) {
    const connectButton = (
      <Button size="sm" onPress={onConnect} fullWidth variant="tertiary" isDisabled={isDisabled}>
        Connect
      </Button>
    )

    if (isDisabled && tooltipMessage) {
      return (
        <Tooltip label={tooltipMessage} placement="bottom" mouseEnterDelay={0}>
          <div className={Styles.tooltipWrapper}>{connectButton}</div>
        </Tooltip>
      )
    }

    return connectButton
  }

  if (isVirtualMarketer) {
    return (
      <CardButtonsForVirtualMarketer
        integration={integration}
        isDisconnected={isDisconnected}
        isOutdated={isOutdated}
      />
    )
  }

  return (
    <Button onPress={() => openModal(ManageIntegrationAccounts, { provider })} variant="tertiary" size="sm" fullWidth>
      Manage Integration
    </Button>
  )
}
